/**
 * balance-environment.ts — v4M13.4a per-kind `EnvironmentEntity`
 * effect constants + the merge helper that produces the effective
 * effect for any specific instance.
 *
 * Authoritative spec: `docs/TERRAIN_FEATURES.md §3` (state shape) +
 * `§1.1–§1.6` (per-kind gameplay-effect locks). The constants here
 * are the **defaults**: scenarios can author per-instance overrides
 * via `EnvironmentEntity.effects` and `effectiveEnvironmentEffect`
 * merges them at read time.
 *
 * **v4M13.4a strict-parity:** module exists + tested, but no
 * advance-pass consumer reads it yet. v4M13.4b's
 * `advance-environment-effects.ts` is the first consumer; v4M13.4c
 * wires nebula invisibility + asteroid LoS-broken into the detection
 * pipeline; v4M13.4d wires AI hazard-awareness into pathfinding +
 * combat target scoring.
 */

import type {
  EnvironmentEffect,
  EnvironmentEntity,
  EnvironmentKind,
  GameState,
  SystemId,
} from '../state/types/index';

/**
 * Per-kind effect defaults per `TERRAIN_FEATURES.md §3.1`.
 *
 * Numeric tuning rationale:
 *   - `asteroid-field.movementSpeedMultiplier = 0.5` — ships at 50%
 *     of normal sublight inside per PLANETS.md §4.1 + TERRAIN_FEATURES
 *     §1.1. Uniform across ship classes (no per-class curve in v1).
 *   - `radiation-belt.hullDamagePerTick = 2.0` — flat 2 hp/tick per
 *     TERRAIN_FEATURES §1.6 (uniform across ship classes, bypasses
 *     shields). Tuning candidate at v4M13.5 playtest.
 *
 * Boolean-effect kinds (nebula / black-hole / ion-cloud /
 * exotic-gas-cloud) carry the flag fields directly; absence of a
 * flag means the effect doesn't apply.
 *
 * Exotic gas cloud entry is intentionally empty — per Michael's
 * 2026-05-31 design decision the type is a pure resource site with
 * no movement / scan / damage hazard.
 */
export const ENVIRONMENT_EFFECT_DEFAULTS: Readonly<
  Record<EnvironmentKind, EnvironmentEffect>
> = Object.freeze({
  'asteroid-field': {
    movementSpeedMultiplier: 0.5,
    blocksLineOfSight: true,
  },
  'exotic-gas-cloud': {
    // Pure resource extraction site; no gameplay-effect hazard.
    // Preserved as an entry (not absent) so consumers can dispatch
    // safely on any EnvironmentKind without an existence check.
  },
  nebula: {
    invisibilityInside: true,
    blocksLineOfSight: true,
  },
  'black-hole': {
    destroyOnEntry: true,
  },
  'ion-cloud': {
    zeroShieldsInside: true,
    breakCloakOnEntry: true,
  },
  'radiation-belt': {
    hullDamagePerTick: 2.0,
  },
});

/**
 * Merge per-kind defaults with the entity's optional `effects`
 * override (TERRAIN_FEATURES §3.2). Returns the kind defaults
 * unchanged when no override is present (common case); otherwise
 * spread-merges the override over the defaults so override fields
 * win and unspecified fields fall back to defaults.
 *
 * Pure — no allocation when the entity has no override field.
 * Allocates one object when override is present (acceptable per
 * `advance-environment-effects.ts` call cadence of once per ship ×
 * environment pair per tick, which stays bounded at typical galaxy
 * scales).
 */
export function effectiveEnvironmentEffect(
  env: EnvironmentEntity,
): EnvironmentEffect {
  const defaults = ENVIRONMENT_EFFECT_DEFAULTS[env.kind];
  if (!env.effects) return defaults;
  return { ...defaults, ...env.effects };
}

/**
 * v4M13.4d — Per-kind AI hazard penalty weights per
 * `TERRAIN_FEATURES.md §5`. Higher = more strongly avoided by AI
 * pathfinding + combat dispatch.
 *
 * Black-hole gets the strongest penalty (instant death). Radiation
 * and ion are medium-strong (gradual damage / shield-strip). Asteroid
 * field is the lightest penalty (movement slowdown + slight
 * self-scouting blindness). Nebula is intentionally ZERO — it's a
 * tactical asset for fleets that can use it for ambush, not a hazard.
 * Exotic-gas-cloud is also zero (pure resource site).
 *
 * Values picked to be additive across env count in a system, and to
 * compose meaningfully with the existing pathfinding edge-weight
 * (typically O(0.1-1) at v1 fuel cost) and combat-picker
 * targetScore (typically O(0-20) at v1 strategic value).
 */
export const AI_HAZARD_PENALTY: Readonly<Record<EnvironmentKind, number>> =
  Object.freeze({
    'asteroid-field': 0.2,
    'exotic-gas-cloud': 0,
    nebula: 0,
    'black-hole': 5.0,
    'ion-cloud': 1.0,
    'radiation-belt': 1.0,
  });

/**
 * Sum of `AI_HAZARD_PENALTY` for every environment in `systemId`.
 * Returns 0 when the system has no hazardous environments (the
 * common case — fast-path).
 *
 * Consumers: `pathfinding.ts:firstStepTowardsWeighted` (adds the
 * destination system's penalty to the edge weight, multiplied by
 * the hop distance) + `pickCombatJumpDestinationThreatWeighted`
 * (subtracts the candidate system's penalty from the target score).
 */
export function aiHazardPenaltyForSystem(
  state: GameState,
  systemId: SystemId,
): number {
  // MFix40.1 — defensive null-coalesce. Production paths always seed
  // `state.environment` via scenario.ts, but minimal test fixtures
  // predating v4M13.4 don't; treat absence as "no hazards".
  if (!state.environment) return 0;
  let total = 0;
  for (const env of state.environment.values()) {
    if (env.systemId !== systemId) continue;
    total += AI_HAZARD_PENALTY[env.kind];
  }
  return total;
}

/**
 * Convenience predicate — does the kind have ANY gameplay effect?
 * Returns false for exotic-gas-cloud (pure resource site).
 *
 * Used by AI hazard-awareness (v4M13.4d) to short-circuit the
 * hazard-penalty calculation when iterating over candidate
 * destinations: a system whose only environments are exotic-gas-
 * clouds has zero hazard contribution regardless of count.
 */
export function kindHasGameplayEffect(kind: EnvironmentKind): boolean {
  const defaults = ENVIRONMENT_EFFECT_DEFAULTS[kind];
  return (
    defaults.movementSpeedMultiplier !== undefined ||
    defaults.blocksLineOfSight === true ||
    defaults.invisibilityInside === true ||
    defaults.destroyOnEntry === true ||
    defaults.zeroShieldsInside === true ||
    defaults.breakCloakOnEntry === true ||
    (defaults.hullDamagePerTick !== undefined && defaults.hullDamagePerTick > 0)
  );
}
